/*
 * @author        david.loehr@zumper.com (David Loehr)
 * @copyright     Copyright (c) 2023, Zumper
 * @description   BottomHalf.tsx
 */

/** @jsx jsx */
import { Box } from '@chakra-ui/react'
import { ClassNames, jsx } from '@emotion/react'
import { useRouteProps, useViewSection } from '@zcode-ui/hook'
import {
  NearbyHomesContainer,
  NearbyHomesLazySection,
  NearbyPetFriendlyContainer,
  NearbyPetFriendlyLazySection,
  RentHistoryLazyLoadableSection,
  selectIsOnlyActiveUnitOfMultiUnit,
  selectListingId,
} from '@zcode-ui/perm-address'
import {
  AffordabilityCalculatorLazyLoadable,
  DETAIL_CONTAINER_WIDTH,
  DetailContainer,
  DualScrollLayoutSimple,
  STICKY_NAV_LAYOUT_SHIFT,
  getMedianPricesByBedroom,
  useMwebDetailPoi,
} from '@zcode-ui/web-zumper-features'
import { selectIsAddressOnBuilding, selectIsMobile } from '@zcode-ui/web-zumper-global-state'
import { RENT_HISTORY_IMPRESSION } from '@zcode-ui/web-zumper-integrations'
import { AIAssistantEntryBottomHalf, FloorplansConditionally } from '@zcode-ui/web-zumper-pages'
import {
  LazyHydratedSection,
  ScrollingNavSection,
  useScrollingNavContext,
} from '@zcode-ui/web-zumper-ui'
import { DETAIL } from '@zcode/mixpanel'
import { AnalyticsC, Origin, localStorageSingleton } from '@zcode/nr-utils'
import { selectGalleryUrlFromLocation } from '@zcode/nr/es/shared/bizos/rentable/selectors'
import { MESSAGE_FORM_INLINE } from '@zcode/nr/es/shared/constants/message'
import type { MaybeEmptyProperty } from '@zcode/state-type'
import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { trackAIAssistantEntryClick } from '../../../components/AIAssistant/thunks/trackAIAssistantEntryClick'
import { trackAIAssistantEntryImpression } from '../../../components/AIAssistant/thunks/trackAIAssistantEntryImpression'
import { ContextualFooterLazyLoadableSection } from '../../../components/ContextualFooter/ContextualFooterLazyLoadableSection'
import { DetailSchoolWidgetLazyLoadableSection } from '../../../components/DetailSchoolWidget/DetailSchoolWidgetLoadableSection'
import { LocalLogicLazyLoadableSection } from '../../../components/LocalLogic/LocalLogicLoadableSection'
import { InactiveSaveSearchStickyContainer as InactiveSaveSearchSticky } from '../../../components/SaveSearch/InactiveSaveSearchSection/InactiveSaveSearchSticky/InactiveSaveSearchStickyContainer'
import { AboutManagementLoadable } from '../../../components/detail/AboutManagement/AboutManagementLazyLoadable'
import { BottomMessageContainer } from '../../../components/detail/BottomMessage/BottomMessageContainer'
import { default as FullAmenitiesLazyLoadableSection } from '../../../components/detail/Content/FullAmenities/FullAmenitiesLazyLoadableSection'
import { useFullAmenitiesProps } from '../../../components/detail/Content/FullAmenities/helpers/useFullAmenitiesProps'
import { IncomeRestrictionsLazyLoadableSection } from '../../../components/detail/Content/IncomeRestrictions/IncomeRestrictionsLazyLoadableSection'
import { PoliciesFeesAndDetails } from '../../../components/detail/Content/PoliciesAndFees/PoliciesFeesAndDetails'
import { DetailAds } from '../../../components/detail/DetailAds/DetailAds'
import { RentTrendsOuterContainer } from '../../../components/detail/RentTrends/RentTrendsOuter/RentTrendsOuterContainer'
import { default as SisterPropertiesLazyLoadableSection } from '../../../components/detail/SisterProperties/SisterPropertiesLazyLoadableSection'
import { StickyFormLoadable } from '../../../components/detail/StickyForm/StickyFormLoadable'
import { useAIAssistant } from '../../../components/detail/hooks/useAIAssistant'
import { FooterLazyLoadableSection } from '../../../components/footer/FooterLazyLoadable'
import { MessageFormLoadable } from '../../../components/message/MessageForm/MessageFormLoadable'
import { selectShouldLimitLeads } from '../../../components/message/MessageForm/helpers/getShouldLimitLeads'
import {
  RelatedPropertiesLazyLoadableSection,
  RelatedPropertiesLoadingFallback,
} from '../../../features/RelatedProperties/RelatedPropertiesLazyLoadableSection'
import { useIsGalleryExperiment } from '../../../features/RelatedProperties/hooks/useIsGalleryExperiment'
import {
  RelatedPropertiesMonetizedLazyLoadableSection,
  RelatedPropertiesMonetizedLoadingFallback,
} from '../../../features/RelatedPropertiesMonetized/RelatedPropertiesMonetizedLazyLoadableSection'
import {
  selectEntity,
  selectEntityFetching,
} from '../../../modules/detail/modules/entity/selectors/index'
import { trackMessageImpression as _trackMessageImpression } from '../../../modules/traktor/thunks/trackMessageImpression'
import { useDispatch } from '../../../modules/useDispatch'
import { getUrlParams } from '../../../utils/urls/getUrlParams'
import { LinkPackLazyLoadableSection } from '../../PageAddress/LinkPack/LinkPackLazyLoadableSection'
import { NZRUnitsTableLazyLoadableSection } from '../../PageAddress/NZRUnitsTableLazyLoadableSection'
import { trackViewPASection } from '../../PageAddress/helpers/trackViewPASection'
import { TelcoWrapper } from '../TelcoWidget/TelcoWrapper'
import { useHigherEducation } from '../hooks/useHigherEducation'
import { AboutLazyLoadableSection } from './AboutLazyLoadableSection'
import * as styles from './styles'

const defaultRootMargin = '1000px' // very generous root margin to load chunks earlier and prevent CLS

export const BottomHalf = () => {
  const dispatch = useDispatch()
  const trackMessageImpression = useCallback(
    (...args: Parameters<typeof _trackMessageImpression>) =>
      dispatch(_trackMessageImpression(...args)),
    [dispatch]
  )
  const entity: MaybeEmptyProperty = useSelector(selectEntity) ?? {}
  const isOnlyActiveUnitOfMultiUnit = useSelector(selectIsOnlyActiveUnitOfMultiUnit)
  const routeProps = useRouteProps()
  const unitModalListingId = selectListingId({ routeProps, searchOnly: true })
  const isLoading = useSelector(selectEntityFetching)
  const {
    formattedAddress,
    incomeRestrictions,
    isActive,
    isBuilding,
    isSignatureSubscriptionPackage,
    waitlistEnabled,
  } = entity
  const hasIncomeRestrictions =
    typeof incomeRestrictions?.length === 'undefined' && incomeRestrictions?.restrictions?.length
  const shortAddress = formattedAddress?.split?.(',')[0]
  const isMobile = useSelector(selectIsMobile)
  const shouldLimitLeads = useSelector(selectShouldLimitLeads)
  const isSingleUnitListing = !isBuilding
  const match = useRouteMatch<{ buildingId?: string; listingId?: string; addressId?: string }>()
  const isListing = Boolean(match?.params?.listingId)
  const isAddress = !isListing && !match?.params?.buildingId
  const urlParamListingId = getUrlParams().listingId
  const { scrollSectionIntoView, sections } = useScrollingNavContext()
  const alreadyScrolled = useRef(false)
  const section = useMemo(() => {
    return sections.find(({ name }) => name === 'Floor plans')
  }, [sections])
  const { shouldHide } = useFullAmenitiesProps({ entity })
  const showNewPOISection = useMwebDetailPoi()
  const shouldRenderSchoolsEarlier = useHigherEducation()
  const history = useHistory()
  const showAIAssistant = !!useAIAssistant(entity)
  const isAddressOnBuilding = useSelector(selectIsAddressOnBuilding)

  const bedroomPrices = useMemo(() => {
    const allListings = isSingleUnitListing
      ? entity.isActive
        ? [entity]
        : []
      : (entity.activeFloorplans ?? [])
    return getMedianPricesByBedroom(allListings)
  }, [entity, isSingleUnitListing])
  const shouldRenderAffCalculator = !!shortAddress && Object.keys(bedroomPrices).length > 0

  const rentHistoryRef = useViewSection(
    useCallback(() => {
      dispatch(
        trackViewPASection({
          traktorName: RENT_HISTORY_IMPRESSION,
          mixpanelName: DETAIL.RENT_HISTORY,
        })
      )
    }, [dispatch])
  )

  const isGalleryExperiment = useIsGalleryExperiment(entity)
  useEffect(() => {
    if (isGalleryExperiment) {
      const url = selectGalleryUrlFromLocation()
      history.push(url(history.location))
      localStorageSingleton.removeItem('similarGroupId')
    }
  }, [isGalleryExperiment])

  useEffect(() => {
    // Scroll to the component when it mounts or when the bedrooms param is in the url params
    if (urlParamListingId && section && !alreadyScrolled.current) {
      scrollSectionIntoView(section)
      alreadyScrolled.current = true
    }
  }, [scrollSectionIntoView, section, urlParamListingId])

  return useMemo(
    () => (
      <Fragment>
        <Box<'div'> ml="auto" mr="auto" width={DETAIL_CONTAINER_WIDTH}>
          {!isMobile && !isSingleUnitListing && (
            <DetailContainer {...STICKY_NAV_LAYOUT_SHIFT}>
              <FloorplansConditionally lazy rootMargin={defaultRootMargin} />
            </DetailContainer>
          )}
          <DualScrollLayoutSimple
            stickyNavLayoutShift="both"
            showSlotSticky={!shouldLimitLeads}
            slotBody={
              <Fragment>
                {showAIAssistant && !isMobile && (
                  <AIAssistantEntryBottomHalf
                    trackAIAssistantEntryClick={trackAIAssistantEntryClick}
                    trackAIAssistantEntryImpression={trackAIAssistantEntryImpression}
                  />
                )}
                {hasIncomeRestrictions && (
                  <ScrollingNavSection continuationId="income" mt={20}>
                    <IncomeRestrictionsLazyLoadableSection
                      isDetailSection
                      rootMargin={defaultRootMargin}
                    />
                  </ScrollingNavSection>
                )}
                {!shouldHide ? (
                  <FullAmenitiesLazyLoadableSection rootMargin={defaultRootMargin} />
                ) : null}
                <ScrollingNavSection mt={20} name={isAddress ? 'Policies' : 'Policies and fees'}>
                  <PoliciesFeesAndDetails />
                </ScrollingNavSection>
                <ScrollingNavSection continuationId="ad after fp" display={{ lg: 'none' }} mt={16}>
                  <DetailAds layout="square" showBorderBottom showBorderTop />
                </ScrollingNavSection>
                <AboutLazyLoadableSection rootMargin={defaultRootMargin} />
                <ScrollingNavSection continuationId="After Contact" mt={20}>
                  {isActive && (
                    <Fragment>
                      <AboutManagementLoadable />
                      {isSignatureSubscriptionPackage && (
                        <SisterPropertiesLazyLoadableSection
                          isDetailPage
                          rootMargin={defaultRootMargin}
                        />
                      )}
                    </Fragment>
                  )}
                  {isActive && (
                    <RentTrendsOuterContainer
                      analyticChannel={AnalyticsC.FULL_DETAIL}
                      rootMargin={defaultRootMargin}
                    />
                  )}
                  {shouldRenderAffCalculator && (
                    <AffordabilityCalculatorLazyLoadable
                      location={shortAddress}
                      bedroomPrices={bedroomPrices}
                    />
                  )}
                </ScrollingNavSection>
              </Fragment>
            }
            slotSticky={
              isActive ? (
                entity.isMessageable ? (
                  <ClassNames>
                    {({ css }) => (
                      <StickyFormLoadable
                        analyticChannel={AnalyticsC.FULL_DETAIL}
                        headerText={waitlistEnabled ? 'Ask to join waitlist' : 'Check availability'}
                        lazySectionClassName={css('height: 100%')}
                        onView={trackMessageImpression}
                        origin={
                          isAddressOnBuilding ? Origin.ADDR_DETAIL_INLINE : Origin.DETAIL_INLINE
                        }
                        rootMargin={defaultRootMargin}
                      />
                    )}
                  </ClassNames>
                ) : null
              ) : (
                <LazyHydratedSection>
                  <Box<'div'> paddingTop={5} top={32}>
                    <InactiveSaveSearchSticky
                      origin={
                        isAddressOnBuilding ? Origin.ADDR_DETAIL_INLINE : Origin.DETAIL_INLINE
                      }
                    />
                  </Box>
                </LazyHydratedSection>
              )
            }
          />
        </Box>

        <Box<'div'> overflowX="hidden" {...STICKY_NAV_LAYOUT_SHIFT}>
          {!isActive && (
            <DetailContainer>
              {isLoading ? (
                <RelatedPropertiesMonetizedLoadingFallback />
              ) : (
                <RelatedPropertiesMonetizedLazyLoadableSection
                  analyticChannel={AnalyticsC.FULL_DETAIL}
                  origin={
                    isAddressOnBuilding
                      ? Origin.ADDR_DETAIL_DESKTOP_MM_NEARBY_PROPERTIES
                      : Origin.DETAIL_DESKTOP_MM_NEARBY_PROPERTIES
                  }
                  rootMargin={defaultRootMargin}
                />
              )}
              <RentTrendsOuterContainer
                analyticChannel={AnalyticsC.FULL_DETAIL}
                rootMargin={defaultRootMargin}
                copyContainerProps={{ maxW: { base: '100%', md: '75%' } }}
              />
            </DetailContainer>
          )}
          <ScrollingNavSection continuationId="rent history">
            {isSingleUnitListing && isAddress && (
              <DetailContainer>
                <div ref={rentHistoryRef}>
                  <RentHistoryLazyLoadableSection rootMargin={defaultRootMargin} shouldExpand />
                </div>
              </DetailContainer>
            )}
          </ScrollingNavSection>
          <ScrollingNavSection name="Neighborhood">
            <DetailContainer>
              {!shouldRenderSchoolsEarlier && (
                <DetailSchoolWidgetLazyLoadableSection
                  isHigherEducationExperiment={false}
                  rootMargin={defaultRootMargin}
                />
              )}
              <DetailAds layout="leaderboard" showBorderTop />
            </DetailContainer>
            <DetailContainer>
              <TelcoWrapper rootMargin={defaultRootMargin} />
            </DetailContainer>
            <Box<'div'> bg={showNewPOISection ? 'gray.0' : 'gray.50'}>
              <DetailContainer>
                <LocalLogicLazyLoadableSection
                  address={entity}
                  localLogicCss={styles.localLogic}
                  poiCss={styles.poi}
                  rootMargin={defaultRootMargin}
                />
              </DetailContainer>
            </Box>

            <DetailContainer>
              {isActive && <BottomMessageContainer analyticChannel={AnalyticsC.FULL_DETAIL} />}
              {(isOnlyActiveUnitOfMultiUnit || unitModalListingId) && (
                <NZRUnitsTableLazyLoadableSection
                  forceInView={unitModalListingId ? true : undefined}
                  rootMargin={defaultRootMargin}
                />
              )}

              {isActive &&
                (isLoading ? (
                  <RelatedPropertiesLoadingFallback />
                ) : (
                  <RelatedPropertiesLazyLoadableSection
                    analyticChannel={AnalyticsC.FULL_DETAIL}
                    origin={
                      isAddressOnBuilding
                        ? Origin.ADDR_DETAIL_MM_RELATED_PROPERTIES
                        : Origin.DETAIL_MM_RELATED_PROPERTIES
                    }
                    rootMargin={defaultRootMargin}
                  />
                ))}

              {isActive &&
                (entity.isMessageable ? (
                  <Box<'div'> display={{ base: 'block', md: 'none' }}>
                    <ClassNames>
                      {({ css }) => (
                        <MessageFormLoadable
                          allowMFTourButton
                          analyticChannel={AnalyticsC.FULL_DETAIL}
                          containerClass={css(
                            `padding: 7px 0 32px;
                      border: none;
                      box-shadow: none;`
                          )}
                          ctaName={waitlistEnabled ? 'Ask to join waitlist' : 'Check availability'}
                          entity={entity}
                          formType={MESSAGE_FORM_INLINE}
                          headerText={
                            waitlistEnabled ? 'Ask to join waitlist' : 'Check availability'
                          }
                          hideCancelButton
                          hideCustomMessage
                          hideMoveInDate
                          isWaitlistForm={waitlistEnabled}
                          onView={trackMessageImpression}
                          origin={
                            isAddressOnBuilding
                              ? Origin.ADDR_DETAIL_MOBILE_BOTTOM
                              : Origin.DETAIL_MOBILE_BOTTOM
                          }
                          rootMargin={defaultRootMargin}
                          showSummary={false}
                          unavailableMsgClass={css('padding: 0')}
                        />
                      )}
                    </ClassNames>
                  </Box>
                ) : null)}
              {!isActive && (
                <Box<'div'> display={{ base: 'block', md: 'none' }}>
                  <InactiveSaveSearchSticky
                    origin={
                      isAddressOnBuilding
                        ? Origin.ADDR_DETAIL_MOBILE_BOTTOM
                        : Origin.DETAIL_MOBILE_BOTTOM
                    }
                  />
                </Box>
              )}
              {!isAddress && (
                <ContextualFooterLazyLoadableSection
                  isDetailPage
                  rootMargin={defaultRootMargin}
                  rowsLimit={9}
                >
                  {isListing && (
                    <Box<'div'> mt={{ base: 12, md: 20 }}>
                      <Box<'div'> mt={{ base: 8, md: 12 }}>
                        <NearbyPetFriendlyContainer />
                      </Box>
                      <Box<'div'> mt={{ base: 8, md: 12 }}>
                        <NearbyHomesContainer />
                      </Box>
                    </Box>
                  )}
                </ContextualFooterLazyLoadableSection>
              )}
            </DetailContainer>

            {isAddress && (
              <Fragment>
                <DetailContainer>
                  <Box<'div'> mb={{ base: 12, md: 20 }}>
                    <LinkPackLazyLoadableSection rootMargin={defaultRootMargin} />
                  </Box>
                </DetailContainer>
                <DetailContainer>
                  <Box<'div'> mt={{ base: 8, md: 12 }}>
                    <NearbyPetFriendlyLazySection rootMargin={defaultRootMargin} />
                  </Box>

                  <Box<'div'> mt={{ base: 8, md: 12 }}>
                    <NearbyHomesLazySection rootMargin={defaultRootMargin} />
                  </Box>
                </DetailContainer>
                <DetailContainer>
                  <Box<'div'> mt={{ base: 20, desktop: 12 }}>
                    <DetailAds layout="twoSquare" />
                  </Box>
                </DetailContainer>
              </Fragment>
            )}
            <Box<'div'> bg="#25323e">
              <Box<'div'>
                mx={{ base: 6, md: 'auto', lg: 8, '2xl': 'auto' }}
                w={{ base: 'initial', md: '719px', lg: 'initial', '2xl': '1376px' }}
              >
                <FooterLazyLoadableSection
                  analyticChannel={AnalyticsC.FULL_DETAIL}
                  rootMargin={defaultRootMargin}
                />
              </Box>
            </Box>
          </ScrollingNavSection>
        </Box>
      </Fragment>
    ),
    [
      bedroomPrices,
      entity,
      hasIncomeRestrictions,
      isActive,
      isAddress,
      isListing,
      isLoading,
      isMobile,
      isOnlyActiveUnitOfMultiUnit,
      isSignatureSubscriptionPackage,
      isSingleUnitListing,
      rentHistoryRef,
      shortAddress,
      shouldHide,
      shouldLimitLeads,
      shouldRenderAffCalculator,
      showNewPOISection,
      trackMessageImpression,
      unitModalListingId,
      waitlistEnabled,
    ]
  )
}
